/**
 * Guest Management + Check-in API client.
 *
 * Mirrors the backend GuestModule / CheckinModule contracts. Uses the Firebase
 * ID token for authenticated calls and a bare fetch for the public pass lookup
 * (which must work for unauthenticated guests). Unwraps the TransformInterceptor
 * `{ data }` envelope like the rest of the app.
 */

import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';

// ── Types (mirror backend dto/guest.dto.ts + checkin.dto.ts) ─────────────────

export interface GuestRowError {
  row: number;
  field: string;
  message: string;
}

export interface ImportGuestsResult {
  imported: number;
  skipped: number;
  errors: GuestRowError[];
}

export interface Guest {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string | null;
  guestCode: string;
  inviteStatus: 'NOT_SENT' | 'SENT' | 'FAILED';
  inviteSentAt: string | null;
  rsvpStatus: 'PENDING' | 'ACCEPTED' | 'DECLINED';
  checkinStatus: 'NOT_CHECKED_IN' | 'CHECKED_IN';
  createdAt: string;
}

export interface GuestStats {
  total: number;
  sent: number;
  checkedIn: number;
  notArrived: number;
}

export interface ListGuestsResponse {
  data: Guest[];
  total: number;
  page: number;
  limit: number;
  stats: GuestStats;
}

export interface GuestPass {
  guestName: string;
  eventName: string;
  eventDate: string | null;
  eventVenue: string | null;
  guestCode: string;
}

export interface VerifyCheckinResponse {
  valid: boolean;
  guestName?: string;
  eventName?: string;
  alreadyCheckedIn: boolean;
  checkedInAt?: string;
  checkedInBy?: string;
}

export interface ConfirmCheckinResponse {
  success: true;
  checkedInAt: string;
}

export interface ListGuestsParams {
  page?: number;
  limit?: number;
  search?: string;
  inviteStatus?: string;
  checkinStatus?: string;
}

// ── Auth helper ───────────────────────────────────────────────────────────────

const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

async function authToken(): Promise<string | null> {
  if (isBypass) return 'dev-token';
  return (await auth.currentUser?.getIdToken().catch(() => null)) ?? null;
}

async function authFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const token = await authToken();
  const res = await fetch(url, {
    ...init,
    headers: {
      ...(init.body instanceof FormData
        ? {}
        : { 'Content-Type': 'application/json' }),
      ...(init.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`;
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

// ── Organiser endpoints ───────────────────────────────────────────────────────

export function guestTemplateUrl(invitationId: string): string {
  return `${API_BASE}/invitations/${invitationId}/guests/template`;
}

/** Download the CSV template as a Blob (auth required), then trigger a save. */
export async function downloadGuestTemplate(invitationId: string): Promise<void> {
  const token = await authToken();
  const res = await fetch(guestTemplateUrl(invitationId), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Failed to download template (HTTP ${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'guest-template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function importGuests(
  invitationId: string,
  file: File,
): Promise<ImportGuestsResult> {
  const form = new FormData();
  form.append('file', file);
  return authFetch<ImportGuestsResult>(
    `${API_BASE}/invitations/${invitationId}/guests/import`,
    { method: 'POST', body: form },
  );
}

export function listGuests(
  invitationId: string,
  params: ListGuestsParams = {},
): Promise<ListGuestsResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return authFetch<ListGuestsResponse>(
    `${API_BASE}/invitations/${invitationId}/guests${suffix}`,
  );
}

export function sendInvitations(
  invitationId: string,
  guestIds?: string[],
): Promise<{ queued: number }> {
  return authFetch<{ queued: number }>(
    `${API_BASE}/invitations/${invitationId}/guests/send-invitations`,
    { method: 'POST', body: JSON.stringify({ guestIds: guestIds ?? [] }) },
  );
}

// ── Public pass ───────────────────────────────────────────────────────────────

export async function getGuestPass(guestCode: string): Promise<GuestPass> {
  const res = await fetch(
    `${API_BASE}/pass/${encodeURIComponent(guestCode)}`,
  );
  if (res.status === 404) throw new Error('NOT_FOUND');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as GuestPass;
}

// ── Check-in ──────────────────────────────────────────────────────────────────

export function verifyCheckin(guestCode: string): Promise<VerifyCheckinResponse> {
  return authFetch<VerifyCheckinResponse>(`${API_BASE}/checkin/verify`, {
    method: 'POST',
    body: JSON.stringify({ guestCode }),
  });
}

export function confirmCheckin(guestCode: string): Promise<ConfirmCheckinResponse> {
  return authFetch<ConfirmCheckinResponse>(`${API_BASE}/checkin/confirm`, {
    method: 'POST',
    body: JSON.stringify({ guestCode }),
  });
}
